import { Injectable, inject } from '@angular/core';
import { LuminariasHttpService } from '../servicios/http/luminarias.service';
import { AlarmasDomiciliariasService } from '../../modulos/entidades/alarmas/alarmas-domiciliarias/alarmas-domiciliarias.service';
import { UltimoReportesGenericosService } from '../servicios/http/ultimo-reportes-genericos.service';
import {
  IFilter,
  IPopulate,
  IQueryParam,
  IReporteGenerico,
  ISirena,
} from 'modelos/src';
import { SirenasHttpService } from '../servicios/http/sirenas.service';
import { IntegracionesService } from '../servicios/http/integraciones.service';
import { SirenaMapa } from '../../modulos/entidades/sirenas/mapa-sirenas/mapa-sirenas.component';
import { ISirenaExtendida } from '../mapbox/entity-managers';

@Injectable({
  providedIn: 'root',
})
export class ObtenerDataEntidadService {
  private luminariasService = inject(LuminariasHttpService);
  private alarmasService = inject(AlarmasDomiciliariasService);
  private ultimosReportesService = inject(UltimoReportesGenericosService);
  private sirenasService = inject(SirenasHttpService);
  private integracionesService = inject(IntegracionesService);

  public async obtenerEntidadPorId(tipo: string, id: string): Promise<any> {
    if (tipo === 'luminaria') {
      return this.obtenerLuminariaPorId(id);
    }
    if (tipo === 'alarma') {
      return this.obtenerAlarmaPorId(id);
    }

    if (tipo === 'colectivo') {
      return this.obtenerReporteColectivoPorId(id);
    }
    if (tipo === 'vehiculo') {
      return this.obtenerReporteVehiculoPorId(id);
    }
    if (tipo === 'activo') {
      return this.obtenerReporteActivosPorId(id);
    }
    if (tipo === 'tracker') {
      return this.obtenerTrackersPorId(id);
    }
    //Sirenas realmente viene como ISirenaExtendida, pero sólo con coordenadas y id.
    //Ese id es el idExterno en sirena de Gestión y _id en ISirena de Seguridad.
    if (tipo === 'sirena') {
      return this.obtenerSirenaExtendida(id);
    }
    if (tipo === 'alertaSeguridad') {
      return this.obtenerAlertaSeguridadPorId(id);
    }
  }

  //8-Alertas de seguridad
  public async obtenerAlertaSeguridadPorId(id: string) {
    return this.integracionesService.getAlertaById(id);
  }

  //Obtener entidades
  //1-Luminarias
  public async obtenerLuminariaPorId(id: string) {
    const luminaria = await this.luminariasService.getById(id);
    return luminaria;
  }
  //2-Alarmas
  public async obtenerAlarmaPorId(id: string) {
    // El popup del mapa muestra el teléfono (sim1/sim2.numero) y el botón
    // "Editar" navega a la edición: la SIM es entidad aparte (FK idSim1/idSim2
    // + virtuals sim1/sim2), hay que pedir populate para que resuelvan.
    const populate: IPopulate[] = [{ path: 'sim1' }, { path: 'sim2' }];
    const query: IQueryParam = { populate: JSON.stringify(populate) };
    const alarma = await this.alarmasService.getById(id, query);
    return alarma;
  }
  //En el caso de activos, vehículos y colectivos, realmente trae el último reporte para mostrar en el mapa
  //3-Colectivos
  public async obtenerReporteColectivoPorId(id: string) {
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Colectivo',
      _id: id,
    };
    const populate: IPopulate = [
      {
        path: 'activo',
        select: 'identificacion categoria vehiculo',
        populate: {
          path: 'vehiculo.recorridos',
          select: 'nombre',
        },
      },
      {
        path: 'grupos',
        select: 'nombre color',
      },
      {
        path: 'tracker',
        select: 'nombre tipo identificacion',
      },
      {
        path: 'cliente',
        select: 'nombre tipoCliente',
      },
      {
        path: 'recorrido',
        select: 'nombre',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      limit: 1,
      includeChildren: true,
      excludeTotalCount: true,
    };

    const listado =
      await this.ultimosReportesService.getFiltered<'Tracker 4G'>(query);
    const colectivo = listado.datos[0];
    return colectivo;
  }
  //4-Vehículos
  public async obtenerReporteVehiculoPorId(id: string) {
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Vehiculo',
      _id: id,
    };
    const populate: IPopulate = [
      {
        path: 'grupos',
        select: 'nombre color',
      },
      {
        path: 'activo',
        select: 'nombre color categoria vehiculo identificacion',
      },
      {
        path: 'tracker',
        select: 'nombre tipo identificacion',
      },
      {
        path: 'cliente',
        select: 'nombre tipoCliente',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      limit: 1,
      includeChildren: true,
      excludeTotalCount: true,
    };

    const listado =
      await this.ultimosReportesService.getFiltered<'Tracker 4G'>(query);
    const vehiculo = listado.datos[0];

    return vehiculo;
  }
  //5-Activos
  public async obtenerReporteActivosPorId(id: string) {
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Activo',
      _id: id,
    };
    const populate: IPopulate = [
      {
        path: 'grupos',
        select: 'nombre color',
      },
      {
        path: 'activo',
        select: 'nombre color categoria identificacion',
      },
      {
        path: 'tracker',
        select: 'nombre tipo identificacion',
      },
      {
        path: 'cliente',
        select: 'nombre tipoCliente',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      limit: 1,
      includeChildren: true,
      excludeTotalCount: true,
    };

    const listado = await this.ultimosReportesService.getFiltered<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >(query);
    const activo = listado.datos[0];
    return activo;
  }
  //6-Activos
  public async obtenerTrackersPorId(id: string) {
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Tracker',
      _id: id,
    };
    const populate: IPopulate = [
      {
        path: 'grupos',
        select: 'nombre color',
      },
      {
        path: 'activo',
        select: 'nombre color categoria identificacion',
      },
      {
        // Editar desde el popup del mapa abre CrearEditarTracker con este
        // tracker: la SIM es entidad aparte (FK idSim1/idSim2 + virtuals
        // sim1/sim2), hay que incluir los FKs en el select y poblar las SIMs.
        path: 'tracker',
        select: 'nombre tipo identificacion idSim1 idSim2',
        populate: [{ path: 'sim1' }, { path: 'sim2' }],
      },
      {
        path: 'cliente',
        select: 'nombre tipoCliente',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      limit: 1,
      includeChildren: true,
      excludeTotalCount: true,
    };

    const listado = await this.ultimosReportesService.getFiltered<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >(query);
    const tracker = listado.datos[0];
    return tracker;
  }
  //7-Sirenas
  public async obtenerSirenaExtendida(id: string) {
    const buildQuery = (filter: IFilter<ISirena>): IQueryParam => ({
      filter: JSON.stringify(filter),
      includeChildren: true,
      limit: 1,
    });

    // a) Buscar en Gestión por idExterno (caso habitual) y fallback por _id (click en mapa)
    const sirenaG =
      (await this.sirenasService.getFiltered(buildQuery({ idExterno: id })))
        .datos[0] ||
      (await this.sirenasService.getFiltered(buildQuery({ _id: id }))).datos[0];

    // b) Buscar en Integraciones por _id de seguridad (idExterno)
    const idSeguridad = sirenaG?.idExterno || id;

    let sirena = (
      await this.integracionesService.getSirenasFiltered(
        buildQuery({ _id: idSeguridad }),
      )
    ).datos[0];

    // Fallback adicional por chipId cuando no hay match por _id
    if (!sirena && sirenaG?.chipId) {
      sirena = (
        await this.integracionesService.getSirenasFiltered(
          buildQuery({ chipId: sirenaG.chipId } as IFilter<ISirena>),
        )
      ).datos[0];
    }

    if (!sirena && !sirenaG) {
      throw new Error(`No se encontró información de sirena para id: ${id}`);
    }

    const coordenadasIntegraciones = (sirena as any)?.geojson?.coordinates;
    const coordenadasGestion = (sirenaG as any)?.ubicacion?.coordinates;

    const coordenadas =
      Array.isArray(coordenadasIntegraciones) &&
      coordenadasIntegraciones.length === 2
        ? ([coordenadasIntegraciones[0], coordenadasIntegraciones[1]] as [
            number,
            number,
          ])
        : Array.isArray(coordenadasGestion) && coordenadasGestion.length === 2
          ? ([coordenadasGestion[0], coordenadasGestion[1]] as [number, number])
          : ([-58.443832, -34.587386] as [number, number]);

    const idGestion = sirenaG?._id || id;
    const idExterno = sirenaG?.idExterno || (sirena as any)?._id || id;
    const idCliente = sirenaG?.idCliente || (sirena as any)?.idCliente;

    // c) Construir estructura extendida para popup/manager
    const sirenaExtendida: ISirenaExtendida = {
      _id: idGestion,
      idCliente,
      idExterno,
      coordenadas,
      ...(sirena || {}),
      popupData: {
        _id: idGestion,
        nombre: (sirenaG as any)?.nombre,
        idExterno,
        idCliente,
        sirena: sirena || undefined,
      },
    };

    return sirenaExtendida;
  }
}
